import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import API, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Beta admin onboarding — single-purpose modal for adding a new FS account.
 *
 * Flow:
 * 1. Display name → auto-suggests slug (admin can override).
 * 2. Slug validated against `^[a-z0-9_]+$` and uniqueness (against existing
 *    `accounts` list passed via prop).
 * 3. Optional onboarding fields: weekly_run_day, primary_domains, search
 *    aliases, website, public/private status, ticker, evidence_mode, notes.
 * 4. POST /api/admin/accounts — toast on success, close modal, parent
 *    refreshes the table.
 *
 * Per spec section 3.4 — slug auto-suggest helper mirrors backend
 * `slugify_account_name` exactly (lowercase, strip dots/hyphens/apostrophes,
 * collapse non-alnum to underscore).
 */
function slugify(name) {
  if (!name) return "";
  return String(name)
    .toLowerCase()
    .trim()
    .replace(/[.'\-]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

const SLUG_RE = /^[a-z0-9_]+$/;

export default function AddAccountModal({ open, onClose, onCreated, existingSlugs = [] }) {
  const [displayName, setDisplayName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [status, setStatus] = useState("coming_soon");
  const [weeklyRunDay, setWeeklyRunDay] = useState("not_scheduled");
  const [primaryDomainsRaw, setPrimaryDomainsRaw] = useState("");
  const [searchAliasesRaw, setSearchAliasesRaw] = useState("");
  const [website, setWebsite] = useState("");
  const [pubPriv, setPubPriv] = useState("unknown");
  const [ticker, setTicker] = useState("");
  const [evidenceMode, setEvidenceMode] = useState("unknown");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset form on open.
  useEffect(() => {
    if (open) {
      setDisplayName("");
      setSlug("");
      setSlugTouched(false);
      setStatus("coming_soon");
      setWeeklyRunDay("not_scheduled");
      setPrimaryDomainsRaw("");
      setSearchAliasesRaw("");
      setWebsite("");
      setPubPriv("unknown");
      setTicker("");
      setEvidenceMode("unknown");
      setNotes("");
      setSubmitting(false);
    }
  }, [open]);

  // Auto-suggest slug from display name unless admin has manually edited it.
  useEffect(() => {
    if (!slugTouched) {
      setSlug(slugify(displayName));
    }
  }, [displayName, slugTouched]);

  // Suggest evidence_mode = private_primary_rich when admin sets pubPriv=private
  // and adds at least one primary domain (per spec section 3.7).
  useEffect(() => {
    if (pubPriv === "private" && primaryDomainsRaw.trim() && evidenceMode === "unknown") {
      setEvidenceMode("private_primary_rich");
    }
  }, [pubPriv, primaryDomainsRaw, evidenceMode]);

  const slugError = useMemo(() => {
    if (!slug) return "";
    if (!SLUG_RE.test(slug)) return "Slug must match ^[a-z0-9_]+$";
    if (existingSlugs.includes(slug)) return "An account with this ID already exists";
    return "";
  }, [slug, existingSlugs]);

  const canSubmit = displayName.trim() && slug.trim() && !slugError && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    const payload = {
      display_name: displayName.trim(),
      account_id: slug.trim(),
      status,
      weekly_run_day: weeklyRunDay === "not_scheduled" ? null : weeklyRunDay,
      primary_domains: primaryDomainsRaw.split(",").map((d) => d.trim()).filter(Boolean),
      search_aliases: searchAliasesRaw.split(",").map((s) => s.trim()).filter(Boolean),
      website: website.trim() || null,
      public_private_status: pubPriv,
      ticker: ticker.trim() || null,
      evidence_mode: evidenceMode,
      notes: notes.trim() || null,
    };
    try {
      const { data } = await API.post("/admin/accounts", payload);
      toast.success(`Account added · ${data.display_name}`);
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/60 px-4"
      role="dialog"
      aria-modal="true"
      data-testid="admin-add-account-modal"
    >
      <div className="bg-white border border-zinc-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h3 className="font-heading text-[20px] font-semibold text-zinc-950" data-testid="admin-add-account-modal-title">Add account</h3>
              <p className="mt-1 text-[12px] text-zinc-600">
                Onboard a new FS account directly from the Admin UI. Primary domains feed the Evidence Quality Gate immediately — no code edit needed.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none px-2"
              data-testid="admin-add-account-modal-close"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="px-6 py-5 space-y-5 text-sm">
            {/* Required */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Display name *</span>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Revolut"
                  className="block w-full px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700"
                  data-testid="admin-add-account-display-name"
                  autoFocus
                  required
                />
              </label>
              <label className="block">
                <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Account ID *</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }}
                  placeholder="auto-suggested from name"
                  className={`block w-full px-3 py-2 border bg-white text-zinc-900 font-mono text-[13px] focus:outline-none ${
                    slugError ? "border-red-500 focus:border-red-600" : "border-zinc-200 focus:border-amber-700"
                  }`}
                  data-testid="admin-add-account-slug"
                  required
                />
                {slugError && (
                  <p className="mt-1 text-[11px] text-red-600" data-testid="admin-add-account-slug-error">{slugError}</p>
                )}
              </label>
            </div>

            <label className="block">
              <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Status</span>
              <div className="flex items-center gap-3" role="radiogroup">
                {[
                  { v: "coming_soon", label: "Coming soon" },
                  { v: "live", label: "Live" },
                ].map((opt) => (
                  <label key={opt.v} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="add-account-status"
                      value={opt.v}
                      checked={status === opt.v}
                      onChange={() => setStatus(opt.v)}
                      data-testid={`admin-add-account-status-${opt.v}`}
                    />
                    <span className="text-[13px] text-zinc-900">{opt.label}</span>
                  </label>
                ))}
              </div>
            </label>

            <div className="border-t border-zinc-100 pt-5">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3">Optional onboarding details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Weekly refresh</span>
                  <select
                    value={weeklyRunDay}
                    onChange={(e) => setWeeklyRunDay(e.target.value)}
                    className="block w-full px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700"
                    data-testid="admin-add-account-schedule"
                  >
                    <option value="not_scheduled">Not scheduled</option>
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Public / private</span>
                  <select
                    value={pubPriv}
                    onChange={(e) => setPubPriv(e.target.value)}
                    className="block w-full px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700"
                    data-testid="admin-add-account-pub-priv"
                  >
                    <option value="unknown">Unknown</option>
                    <option value="public_listed">Public listed</option>
                    <option value="private">Private</option>
                  </select>
                </label>
              </div>

              <label className="block mt-4">
                <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Primary domains (comma-separated)</span>
                <input
                  type="text"
                  value={primaryDomainsRaw}
                  onChange={(e) => setPrimaryDomainsRaw(e.target.value)}
                  placeholder="e.g. revolut.com, revolut.co.uk"
                  className="block w-full px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700"
                  data-testid="admin-add-account-domains"
                />
                <p className="mt-1 text-[11px] text-zinc-500">Sources from these domains score 5 (account primary) in the Evidence Quality Gate.</p>
              </label>

              <label className="block mt-4">
                <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Search aliases (comma-separated)</span>
                <input
                  type="text"
                  value={searchAliasesRaw}
                  onChange={(e) => setSearchAliasesRaw(e.target.value)}
                  placeholder="e.g. Revolut Group, Revolut Bank, Revolut UK"
                  className="block w-full px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700"
                  data-testid="admin-add-account-aliases"
                />
                <p className="mt-1 text-[11px] text-zinc-500">Used by harvest to broaden search where the account's name varies in public sources.</p>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <label className="block">
                  <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Website</span>
                  <input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://www.revolut.com"
                    className="block w-full px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700"
                    data-testid="admin-add-account-website"
                  />
                </label>
                <label className="block">
                  <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Ticker</span>
                  <input
                    type="text"
                    value={ticker}
                    onChange={(e) => setTicker(e.target.value)}
                    placeholder="LSE/NYSE ticker (if listed)"
                    className="block w-full px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700"
                    data-testid="admin-add-account-ticker"
                  />
                </label>
              </div>

              <label className="block mt-4">
                <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Evidence mode</span>
                <select
                  value={evidenceMode}
                  onChange={(e) => setEvidenceMode(e.target.value)}
                  className="block w-full px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700"
                  data-testid="admin-add-account-evidence-mode"
                >
                  <option value="unknown">Unknown</option>
                  <option value="public_listed">Public listed</option>
                  <option value="private_primary_rich">Private, primary-source rich</option>
                  <option value="private_press_led">Private, press-led</option>
                  <option value="thin_public_record">Thin public record</option>
                </select>
              </label>

              <label className="block mt-4">
                <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-1">Notes</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="block w-full px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700 resize-y"
                  data-testid="admin-add-account-notes"
                  placeholder="Anything beta-only worth recording (e.g. high-growth fintech, press-heavy coverage)..."
                />
              </label>
            </div>
          </div>

          <div className="border-t border-zinc-200 px-6 py-4 flex items-center justify-end gap-2 bg-zinc-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[13px] font-medium text-zinc-700 hover:text-zinc-950 transition-colors"
              data-testid="admin-add-account-cancel"
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="rounded-none bg-amber-700 hover:bg-amber-800 text-white text-[13px] font-medium px-4 py-2 h-auto disabled:opacity-50"
              data-testid="admin-add-account-submit"
            >
              {submitting ? "Saving…" : "Add account"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
